export function generatePremiumFxCSS(): string {
  return `
/* ============================================
   ELITE POLISH (Premium Feel)
   ============================================ */

/* Ambient background glows (subtle, image-free) */
body {
  position: relative;
  isolation: isolate;
}
body::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  opacity: var(--fx-ambient-opacity, 0.85);
  background:
    radial-gradient(900px 500px at 10% 0%, rgb(var(--c-primary-rgb) / 0.08), transparent 60%),
    radial-gradient(800px 480px at 90% 10%, rgb(var(--c-accent-rgb) / 0.06), transparent 55%),
    radial-gradient(1100px 700px at 50% 120%, rgb(var(--c-primary-rgb) / 0.05), transparent 65%);
}

/* Luxury mode: slightly sharper contrast */
[data-style="luxe"] .btn--secondary,
[data-style="glass"] .btn--secondary {
  border-color: var(--c-border);
}
[data-style="luxe"] .service-card,
[data-style="glass"] .service-card {
  border-color: var(--c-border);
}


/* ============================================
   PREMIUM FX SYSTEM v2.0 – AGENCY GRADE
   Swiss Precision: Gradient Orbs, Grain, Glass
   ============================================ */

/* Premium FX Tokens – Swiss Finance Grade */
:root {
  /* Orbs: weniger "Instagram", mehr "Schweizer Präzision" */
  --fx-orb-size: clamp(280px, 34vw, 520px);
  --fx-orb-blur: 120px;
  --fx-orb-opacity: 0.09;
  --fx-orb-ease: cubic-bezier(0.22, 1, 0.36, 1);

  /* Grain: feiner + weniger Opacity */
  --fx-grain-opacity: 0.022;
  --fx-grain-size: 240px;

  /* Glass: Finance-tauglich, sehr subtil */
  --fx-glass-blur: 14px;
  --fx-glass-bg: rgba(255, 255, 255, 0.78);
  --fx-glass-border: rgba(255, 255, 255, 0.22);

  /* Glow */
  --fx-glow-opacity: 0.10;
}

.dark {
  --fx-orb-opacity: 0.07;
  --fx-grain-opacity: 0.028;
  --fx-glow-opacity: 0.12;
  --fx-glass-bg: rgba(11, 15, 23, 0.72);
  --fx-glass-border: rgba(255, 255, 255, 0.08);
}

/* ============================================
   GRADIENT ORBS – Atmospheric Light Sources
   ============================================ */

html:has(.fx-orbs) {
  overflow-x: clip;
}

.fx-orbs {
  position: relative;
}

.fx-orbs::before,
.fx-orbs::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  filter: blur(var(--fx-orb-blur));
  opacity: var(--fx-orb-opacity);
  pointer-events: none;
  z-index: 0;
  will-change: transform;
}

/* Primary Orb - Top Right, mehr Offscreen = atmosphärischer */
.fx-orbs::before {
  width: var(--fx-orb-size);
  height: var(--fx-orb-size);
  top: -18vh;
  right: -16vw;
  background: radial-gradient(circle at center, var(--c-primary) 0%, transparent 70%);
  animation: fx-orb-float-1 34s var(--fx-orb-ease) infinite;
}

/* Accent Orb - Bottom Left, symmetrisches Gegengewicht */
.fx-orbs::after {
  width: calc(var(--fx-orb-size) * 0.85);
  height: calc(var(--fx-orb-size) * 0.85);
  bottom: -22vh;
  left: -14vw;
  background: radial-gradient(circle at center, var(--c-accent) 0%, transparent 70%);
  animation: fx-orb-float-2 44s var(--fx-orb-ease) infinite;
}

/* Third Orb: Hidden by default (Pink = Template-Look) */
.fx-orb-3 {
  display: none;
}

@keyframes fx-orb-float-1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(-4%, 6%) scale(1.03); }
  50% { transform: translate(3%, 3%) scale(0.97); }
  75% { transform: translate(-2%, -4%) scale(1.01); }
}

@keyframes fx-orb-float-2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(6%, -5%) scale(1.04); }
  66% { transform: translate(-3%, 4%) scale(0.96); }
}

/* Orb Intensity Variants – Agency Grade */
.fx-orbs-subtle { --fx-orb-opacity: 0.06; --fx-orb-blur: 140px; }
.fx-orbs-medium { --fx-orb-opacity: 0.09; --fx-orb-blur: 120px; }
.fx-orbs-intense { --fx-orb-opacity: 0.14; --fx-orb-blur: 100px; }

/* ============================================
   GRAIN / NOISE – Premium Film Finish
   ============================================ */

.fx-grain-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  pointer-events: none;
  opacity: var(--fx-grain-opacity);
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: var(--fx-grain-size) var(--fx-grain-size);
  mix-blend-mode: soft-light;
}

/* Grain Intensity Variants – Agency Grade */
.fx-grain-subtle .fx-grain-overlay { opacity: 0.018; }
.fx-grain-medium .fx-grain-overlay { opacity: 0.024; }
.fx-grain-strong .fx-grain-overlay { opacity: 0.035; }

/* ============================================
   GLASSMORPHISM – Swiss Clean
   ============================================ */

.fx-glass {
  background: var(--fx-glass-bg);
  backdrop-filter: blur(var(--fx-glass-blur));
  -webkit-backdrop-filter: blur(var(--fx-glass-blur));
  border: 1px solid var(--fx-glass-border);
  box-shadow:
    0 1px 1px rgba(2, 6, 23, 0.04),
    0 10px 30px rgba(2, 6, 23, 0.06);
}

/* Glass Cards - Finance-tauglich, subtil */
.fx-glass-cards .service-card,
.fx-glass-cards .testimonial,
.fx-glass-cards .faq-item {
  background: var(--fx-glass-bg);
  backdrop-filter: blur(var(--fx-glass-blur));
  -webkit-backdrop-filter: blur(var(--fx-glass-blur));
  border: 1px solid var(--fx-glass-border);
  box-shadow:
    0 1px 1px rgba(2, 6, 23, 0.04),
    0 10px 30px rgba(2, 6, 23, 0.06);
}

/* Glass Header */
.fx-glass-header .header {
  background: var(--fx-glass-bg);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-bottom: 1px solid var(--fx-glass-border);
}

/* ============================================
   AMBIENT GLOW - Section color glows
   ============================================ */

.fx-glow {
  position: relative;
}

.fx-glow::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 60%;
  background: radial-gradient(
    ellipse at center top,
    rgba(var(--c-primary-rgb) / var(--fx-glow-opacity)) 0%,
    transparent 70%
  );
  pointer-events: none;
  z-index: 0;
}

.fx-glow > * {
  position: relative;
  z-index: 1;
}

/* Glow Variants */
.fx-glow-accent::before {
  background: radial-gradient(
    ellipse at center top,
    rgba(var(--c-accent-rgb) / var(--fx-glow-opacity)) 0%,
    transparent 70%
  );
}

.fx-glow-bottom::before {
  top: auto;
  bottom: 0;
  background: radial-gradient(
    ellipse at center bottom,
    rgba(var(--c-primary-rgb) / var(--fx-glow-opacity)) 0%,
    transparent 70%
  );
}

/* ============================================
   COMBINED PREMIUM PRESETS
   ============================================ */

/* Premium Mode: All effects combined tastefully */
body.fx-premium {
  --fx-orb-opacity: 0.12;
  --fx-grain-opacity: 0.03;
}

body.fx-premium .hero { overflow: hidden; }

body.fx-premium .hero::before,
body.fx-premium .hero::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  filter: blur(var(--fx-orb-blur));
  opacity: var(--fx-orb-opacity);
  pointer-events: none;
  z-index: 0;
}

body.fx-premium .hero::before {
  width: var(--fx-orb-size);
  height: var(--fx-orb-size);
  top: -15%;
  right: -10%;
  background: radial-gradient(circle, var(--c-primary) 0%, transparent 70%);
  animation: fx-orb-float-1 25s ease-in-out infinite;
}

body.fx-premium .hero::after {
  width: calc(var(--fx-orb-size) * 0.7);
  height: calc(var(--fx-orb-size) * 0.7);
  bottom: 10%;
  left: -5%;
  background: radial-gradient(circle, var(--c-accent) 0%, transparent 70%);
  animation: fx-orb-float-2 30s ease-in-out infinite;
}

/* Premium Grain Overlay */
body.fx-premium::after {
  content: '';
  position: fixed;
  inset: 0;
  z-index: 9998;
  pointer-events: none;
  opacity: var(--fx-grain-opacity);
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
}

/* Premium Glass Cards */
body.fx-premium .service-card,
body.fx-premium .testimonial {
  background: var(--fx-glass-bg);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--fx-glass-border);
}

/* ============================================
   REDUCED MOTION - Respect user preferences
   ============================================ */

@media (prefers-reduced-motion: reduce) {
  .fx-orbs::before,
  .fx-orbs::after,
  .fx-orb-3,
  body.fx-premium .hero::before,
  body.fx-premium .hero::after {
    animation: none;
  }

  .fx-grain-animated::before {
    animation: none;
  }
}
`;
}
